import { Box, Paper, Tab, Tabs, Typography, Grid, MenuItem } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FerramentaDetalhe } from '../../../shared/components';
import { LoadingComp } from '../../../shared/components/loading/Loading';
import { GenericPermissionBox } from '../../../shared/components/permissionBox/GenericPermissionBox';
import { MenuPermissionsBox } from '../../../pages/permissions/components/MenuPermissionsBox';
import { PERMISSIONS_CONFIG } from '../../../shared/config/permissions-config';
import { PermissionActionsEnum, PermissionRolesEnum } from '../../../shared/enums/profiles';
import { useVForm, VForm, VTextField } from '../../../shared/forms';
import { checkByProfileGuard } from '../../../shared/guards/ProfileGuard';
import { useToast } from '../../../shared/hooks';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { clientTypeService, IListagemClientType } from '../../../shared/services/api/clientType/clientTypeService';
import { PermissionServices } from '../../../shared/services/api/permissions/PermissionService';
import { useAuthContext } from '../../../context';
import { useProfile } from '../../../context/ProfileContext';

export const PermissionsDetailsPresentation: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const [permission, setPermission] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [originalClientTypeSelectionId, setOriginalClientTypeSelectionId] = useState<string>('');
  const navigate = useNavigate();
  const { formRef, save } = useVForm();
  const isEdit = useMemo(() => {
    return id !== 'novo';
  }, [id]);

  // We only need handlePermissionsData if we use it for saving, but we are using local state 'permission'
  const { } = usePermissions();

  const toastHandler = useToast();
  const { handleRefreshToken } = useAuthContext();
  const { permissions, isPermissionInitialized, isSuperAdmin } = useProfile();
  const [clientTypes, setClientTypes] = useState<IListagemClientType[]>([]);
  const objectPermission = { clientId: null, level: null };
  const [permissionIdlevel, setPermissionIdlevel] = useState(objectPermission);

  const canCreate = useMemo(() => {
    if (!isPermissionInitialized) return true;
    const role = permissions?.[PermissionRolesEnum.PermissionsOptions];
    return !!role?.[PermissionActionsEnum.Add];
  }, [isPermissionInitialized, permissions]);

  const canUpdate = useMemo(() => {
    if (!isPermissionInitialized) return true;
    const role = permissions?.[PermissionRolesEnum.PermissionsOptions];
    return !!role?.[PermissionActionsEnum.Update];
  }, [isPermissionInitialized, permissions]);

  const isAdminClientType = (type: IListagemClientType | undefined): boolean => {
    return (type?.clientTypeId ?? '').toLowerCase() === 'admin';
  };

  const handlePermissionChange = (moduleKey: string, actionKey: string, value: boolean) => {
    setPermission((prev: any) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [actionKey]: value
      }
    }));
  };

  const handleRequestOptions = () => {
    return JSON.stringify(permission);
  };

  const validateRequiredFields = (): boolean => {
    const formData = formRef.current?.getData();
    const description = String(formData?.description ?? '').trim();
    const clientTypeSelectionId = String(formData?.clientTypeId ?? '').trim();
    const status = String(formData?.status ?? '').trim();

    const errors: Record<string, string> = {};
    if (!description) errors.description = 'Obrigatório';
    if (!clientTypeSelectionId) errors.clientTypeId = 'Obrigatório';
    if (!status) errors.status = 'Obrigatório';

    if (Object.keys(errors).length > 0) {
      formRef.current?.setErrors(errors);
      toastHandler.present({
        type: 'error',
        messageOrContent: 'Preencha Descrição, Nível e Status para salvar.',
        position: 'top-right'
      });
      return false;
    }

    const selectedClientType = clientTypes.find((ct) => String(ct.id) === String(clientTypeSelectionId));
    if (!isSuperAdmin && isAdminClientType(selectedClientType)) {
      formRef.current?.setErrors({ clientTypeId: 'Você não pode selecionar perfil Administrador.' });
      toastHandler.present({
        type: 'error',
        messageOrContent: 'Apenas usuários admin podem cadastrar/alterar permissões do perfil Administrador.',
        position: 'top-right'
      });
      return false;
    }

    if (!isSuperAdmin && isEdit && originalClientTypeSelectionId && String(clientTypeSelectionId) !== String(originalClientTypeSelectionId)) {
      formRef.current?.setErrors({ clientTypeId: 'Somente admin pode alterar o nível.' });
      toastHandler.present({
        type: 'error',
        messageOrContent: 'Somente usuários admin podem alterar o campo Nível.',
        position: 'top-right'
      });
      return false;
    }

    formRef.current?.setErrors({});
    return true;
  };

  const submit = async (): Promise<boolean> => {
    if (!validateRequiredFields()) return false;

    const formData = formRef.current?.getData();
    const permissionsOptionsData = handleRequestOptions();

    const clientTypeSelectionId = String(formData?.clientTypeId ?? '').trim();
    const selectedClientType = clientTypes.find((ct) => String(ct.id) === String(clientTypeSelectionId));
    const resolvedClientTypeId = selectedClientType?.clientTypeId ?? formData?.clientTypeId;

    const clientTypeIdToSend = (!isSuperAdmin && isEdit && originalClientTypeSelectionId)
      ? (clientTypes.find((ct) => String(ct.id) === String(originalClientTypeSelectionId))?.clientTypeId ?? resolvedClientTypeId)
      : resolvedClientTypeId;

    const commonData = {
      description: formData?.description,
      permissions: permissionsOptionsData,
      clientTypeId: clientTypeIdToSend,
    };

    if (id === 'novo') {
      const data = {
        ...commonData,
        level: 0,
        clientId: 0,
      };
      const result = await PermissionServices.create(data);

      if (result instanceof Error) {
        throw result;
      }

      return true;
    } else {
      const data = {
        ...commonData,
        level: permissionIdlevel?.level,
        clientId: permissionIdlevel?.clientId,
      };
      const result = await PermissionServices.updateById(id, data);

      if (result instanceof Error) {
        throw result;
      }

      await handleRefreshToken();

      return true;
    }
  };

  const handleSubmitAndStay = async () => {
    try {
	  const ok = await submit();
	  if (!ok) return;
      toastHandler.present({ type: 'success', messageOrContent: isEdit ? 'Permissão atualizada com sucesso!' : 'Permissão criada com sucesso!', position: 'top-right' });
    } catch (error: any) {
      toastHandler.present({ type: 'error', messageOrContent: `Erro ao ${isEdit ? 'atualizar' : 'criar'} permissão: ${error.message}`, position: 'top-right' });
    }
  };

  const handleSubmitAndNavigateBack = async () => {
    try {
	  const ok = await submit();
	  if (!ok) return;
      toastHandler.present({ type: 'success', messageOrContent: isEdit ? 'Permissão atualizada com sucesso!' : 'Permissão criada com sucesso!', position: 'top-right' });
      navigate('/permissoes');
    } catch (error: any) {
      toastHandler.present({ type: 'error', messageOrContent: `Erro ao ${isEdit ? 'atualizar' : 'criar'} permissão: ${error.message}`, position: 'top-right' });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      try {
        if (id !== 'novo') {
          const [result, types] = await Promise.all([
            PermissionServices.getById(id),
            clientTypeService.getAll()
          ]);
          if (!isMounted) return;

          const clientTypesData = types.data ?? [];
          setClientTypes(clientTypesData);

          const rawClientTypeId = String((result as any)?.clientTypeId ?? '');
          const foundByClientTypeId = clientTypesData.find((ct) => String(ct.clientTypeId) === rawClientTypeId);
          const foundById = clientTypesData.find((ct) => String(ct.id) === rawClientTypeId);
          const resolvedClientType = foundByClientTypeId ?? foundById;
          const normalizedSelectionId = String(resolvedClientType?.id ?? '');

          formRef.current?.setFieldValue('description', (result as any).description);
          formRef.current?.setFieldValue('clientId', (result as any).clientId);
          formRef.current?.setFieldValue('status', (result as any).status ?? 'active');
          formRef.current?.setFieldValue('clientTypeId', normalizedSelectionId);
          formRef.current?.setErrors({});

          setPermissionIdlevel({
            clientId: (result as any).clientId || "0",
            level: (result as any).level,
          });
          setOriginalClientTypeSelectionId(normalizedSelectionId);

          let parsedPermissions: any = {};
          try {
            parsedPermissions = typeof (result as any).permissions === 'string' ? JSON.parse((result as any).permissions) : ((result as any).permissions ?? {});
          } catch {
            parsedPermissions = {};
          }

      // Migração legado -> novo padrão: contratos saem de clients_options e passam a contracts_options + contracts_menu
      try {
        const legacyClientsOptions = parsedPermissions?.clients_options ?? {};
        const legacyHasAnyContracts =
          legacyClientsOptions?.[PermissionActionsEnum.Contracts] ||
          legacyClientsOptions?.[PermissionActionsEnum.ContractsAdd] ||
          legacyClientsOptions?.[PermissionActionsEnum.ContractsUpdate] ||
          legacyClientsOptions?.[PermissionActionsEnum.ContractsShoot];

        if (!parsedPermissions?.[PermissionRolesEnum.ContractsOptions] && legacyHasAnyContracts) {
          parsedPermissions[PermissionRolesEnum.ContractsOptions] = {
            [PermissionActionsEnum.Contracts]: !!legacyClientsOptions?.[PermissionActionsEnum.Contracts],
            [PermissionActionsEnum.ContractsDetails]: !!legacyClientsOptions?.[PermissionActionsEnum.Contracts],
            [PermissionActionsEnum.ContractsAdd]: !!legacyClientsOptions?.[PermissionActionsEnum.ContractsAdd],
            [PermissionActionsEnum.ContractsUpdate]: !!legacyClientsOptions?.[PermissionActionsEnum.ContractsUpdate],
            [PermissionActionsEnum.ContractsShoot]: !!legacyClientsOptions?.[PermissionActionsEnum.ContractsShoot],
          };
        }

        if (!parsedPermissions?.[PermissionRolesEnum.ContractsMenu] && legacyHasAnyContracts) {
          parsedPermissions[PermissionRolesEnum.ContractsMenu] = {
            [PermissionActionsEnum.View]: true,
          };
        }
      } catch {
        // silencioso: não bloqueia a tela
      }

          setPermission(parsedPermissions);
        } else {
          const response = await clientTypeService.getAll();
          if (!isMounted) return;
          setClientTypes(response.data);
          setOriginalClientTypeSelectionId('');

          formRef.current?.setFieldValue('description', '');
          formRef.current?.setFieldValue('clientTypeId', '');
          formRef.current?.setFieldValue('status', 'active');
          formRef.current?.setErrors({});
          setPermissionIdlevel(objectPermission);
          setPermission({});
        }
      } catch (error: any) {
        if (!isMounted) return;
        toastHandler.present({ type: 'error', messageOrContent: `Erro ao consumir dados iniciais: ${error.message}`, position: 'top-right' });
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!isPermissionInitialized) return;

    const allowed = id === 'novo' ? canCreate : canUpdate;
    if (!allowed) {
      toastHandler.present({
        type: 'error',
        messageOrContent: 'Você não tem permissão para acessar esta tela.',
        position: 'top-right'
      });
      navigate('/permissoes', { replace: true });
    }
  }, [id, isPermissionInitialized, canCreate, canUpdate, navigate]);

  const menuConfigs = PERMISSIONS_CONFIG.filter(c => c.type === 'menu');
  const optionConfigs = PERMISSIONS_CONFIG.filter(c => c.type === 'options');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <LayoutBaseDePagina
      titulo={'Permissões'}
      subtitulo={id === 'novo' ? 'Cadastro de nova permissão' : 'Alteração de Permissão'}
      barraDeFerramentas={
        <FerramentaDetalhe
          showButtonNew={id !== 'novo' && checkByProfileGuard(PermissionRolesEnum.PermissionsOptions, PermissionActionsEnum.Add)}
          showBotaoSalvarFechar={id === 'novo' ? canCreate : canUpdate}
          showBotaoSalvar={id === 'novo' ? canCreate : canUpdate}
          showBotaoApagar={false}
          clicarNovo={() => navigate('/permissoes/detalhe/novo')}
          clicarVoltar={() => navigate('/permissoes')}
          clicarSalvar={handleSubmitAndStay}
          clicarSalvarFechar={handleSubmitAndNavigateBack}
        />
      }
    >
      <>
        <LoadingComp isopen={isLoading} />

        <Box sx={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <VForm
            id="formIdSave"
            placeholder="Your placeholder"
            onPointerEnterCapture={() => { }}
            onPointerLeaveCapture={() => { }}
            ref={formRef} onSubmit={save}
          >
            <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" padding={2}>

              <Grid container item direction="row" spacing={2}>
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <VTextField
                    fullWidth
                    label='Descrição'
                    name="description"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <VTextField
                    fullWidth
                    label='Nível'
                    name="clientTypeId"
                    select
                    disabled={isEdit ? !isSuperAdmin : false}
                  >
                    {clientTypes
                      ?.filter((type) => (isSuperAdmin ? true : !isAdminClientType(type)))
                      .map((type) => (
                        <MenuItem key={type.id} value={type.id}>{`${type.description} (${type.level})`}</MenuItem>
                      ))}
                  </VTextField>
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                  <VTextField
                    fullWidth
                    label='Status'
                    name="status"
                    select
                  >
                    <MenuItem value={'active'}>Ativo</MenuItem>
                    <MenuItem value={'inactive'}>Inativo</MenuItem>
                  </VTextField>
                </Grid>
              </Grid>
            </Box>
          </VForm>

          <Box margin={1} component={Paper} variant="outlined" sx={{ display: 'flex', height: '70vh', overflow: 'hidden' }}>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Vertical permission tabs"
              sx={{ borderRight: 1, borderColor: 'divider', minWidth: 200, height: '100%' }}
            >
              <Tab label="Acesso aos Menus" sx={{ alignItems: 'flex-start', textAlign: 'left' }} />
              {optionConfigs.map((config) => (
                <Tab key={config.key} label={config.label} sx={{ alignItems: 'flex-start', textAlign: 'left' }} />
              ))}
            </Tabs>

            <Box padding={3} sx={{ flexGrow: 1, height: '100%', overflowY: 'auto' }}>
              {activeTab === 0 && (
                <MenuPermissionsBox
                  configs={menuConfigs}
                  permissions={permission}
                  onChange={handlePermissionChange}
                />
              )}

              {optionConfigs.map((config, index) => (
                <GenericPermissionBox
                  key={config.key}
                  config={config}
                  permissions={permission}
                  onChange={handlePermissionChange}
                  display={activeTab === index + 1 ? '' : 'none'}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </>
    </LayoutBaseDePagina>
  );
};
